/**
 *  @swagger
 *  definitions:
 *    CustomPageParams:
 *      type: object
 *      properties:
 *        RegionId:
 *          description: ID of the associated Region
 *          type: string
 *        Public:
 *          description: Public of the CustomPage
 *          type: boolean
 *        Name:
 *          description: Name of the CustomPage
 *          type: string
 *        Slug:
 *          description: Slug of the CustomPage
 *          type: string
 *        Content:
 *          description: Content of the CustomPage
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    CustomPageCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/CustomPageParams'
 *        - type: object
 *          required:
 *            - RegionId
 *            - Name
 *
 *  @swagger
 *  definitions:
 *    CustomPageQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the CustomPage
 *              type: string
 *        - $ref: '#/definitions/CustomPageParams'
 *
 *  @swagger
 *  definitions:
 *    CustomPageUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/CustomPageQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    CustomPage:
 *      allOf:
 *        - $ref: '#/definitions/CustomPageCreateParams'
 *        - $ref: '#/definitions/CustomPageUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      CustomPage:
 *        allOf:
 *          - $ref: '#/definitions/CustomPage'
 *          - type: object
 *            properties:
 *              Region:
 *                description: Region of the Championship
 *                $ref: '#/components/schemas/Region'
 */

// Helpers
import {
  validateSlug,
} from '../helpers';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegionId: string;
};

export type $Params = {
  Content: null | string;
  Name: string;
  Public: boolean;
  Slug: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Region?: $Model<'Region'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Content: {
    allowNull: true,
    type: Sequelize.TEXT,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Slug: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'CustomPage'> => {
  const CustomPage = stack.define(
    'CustomPage',
    fields(Sequelize),
    {
      tableName: 'CustomPages',
    },
  );

  CustomPage.associate = (models: $ModelClasses) => {
    models.CustomPage.belongsTo(
      models.Region,
      {
        as: 'Region',
        foreignKey: 'RegionId',
      },
    );
  };

  CustomPage.hooks = (models: $ModelClasses) => {
    CustomPage.addHook(
      'beforeValidate',
      async (customPage: $Model<'CustomPage'>) => {
        if (typeof customPage.Slug !== 'undefined') {
          let currentValue: null | string = null;

          if (customPage.id) {
            currentValue = await CustomPage.findByPk(customPage.id).Slug;
          }

          /* eslint-disable-next-line no-param-reassign,require-atomic-updates */
          customPage.Slug = await validateSlug({
            currentValue,
            models,
            newValue: customPage.Slug,
          });
        }
      },
    );
  };

  CustomPage.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return CustomPage;
});
